import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { logout, getProviderStatus } from "@/lib/api";
import { ChartLineUp, Gauge, SignOut } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";

export default function Layout() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const { data: provider } = useQuery({
    queryKey: ["provider"],
    queryFn: getProviderStatus,
    refetchOnWindowFocus: false,
  });

  const handleLogout = async () => {
    try {
      await logout();
    } catch {}
    setUser(null);
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--rt-bg)] text-[var(--rt-text)]">
      <header className="border-b border-[var(--rt-border)] bg-white sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-logo">
              <img
                src="/brand/geokath-logo.jpg"
                alt="Geokath"
                className="h-8 w-auto object-contain"
              />
              <span className="hidden md:inline text-xs label-eyebrow border-l border-[var(--rt-border)] pl-2.5">
                Rank Intelligence
              </span>
            </Link>
            <nav className="flex items-center gap-6 text-sm">
              <NavLink
                to="/dashboard"
                data-testid="nav-dashboard"
                className={({ isActive }) =>
                  `pb-1 border-b-2 ${isActive ? "border-[var(--rt-primary)] text-[var(--rt-text)]" : "border-transparent text-[var(--rt-text-muted)] hover:text-[var(--rt-text)]"}`
                }
              >
                Websites
              </NavLink>
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 text-xs label-eyebrow">
              <span
                className="inline-block w-2 h-2"
                style={{ background: provider?.configured ? "var(--rt-success)" : "var(--rt-warning)" }}
              />
              <span data-testid="provider-status">
                {provider?.configured ? "DataForSEO live" : "Awaiting credentials"}
              </span>
            </div>

            {user && (
              <div className="flex items-center gap-3 pl-4 border-l border-[var(--rt-border)]">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
                ) : (
                  <div className="w-7 h-7 bg-zinc-200 flex items-center justify-center text-xs font-semibold">
                    {(user.name || user.email)[0]?.toUpperCase()}
                  </div>
                )}
                <span className="text-sm hidden md:inline" data-testid="user-name">{user.name || user.email}</span>
                <button
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="text-[var(--rt-text-muted)] hover:text-[var(--rt-danger)] transition-colors"
                  aria-label="Sign out"
                >
                  <SignOut size={18} />
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-[var(--rt-border)] bg-white">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between text-xs text-[var(--rt-text-muted)]">
          <div className="flex items-center gap-2">
            <ChartLineUp size={14} />
            <span>Weekly auto-tracking · Mon 02:00 UTC</span>
          </div>
          <div className="font-mono-data">
            Next run: {provider?.next_scheduled_run_utc ? new Date(provider.next_scheduled_run_utc).toLocaleString() : "—"}
          </div>
        </div>
      </footer>
    </div>
  );
}
